import React, { useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { Context as RestaurantContext } from '../context/restaurant';
import { Context as CheckoutContext } from '../context/checkout';
import { Context as AuthContext } from '../context/auth';
import { Context as AlertContext } from '../context/alert';
import MainButton from '../components/buttons/main';
import RestaurantHeader from '../components/blocks/restaurantHeader';
import MainLayout from '../layouts/mainLayout';
import { PriceUnderline } from '../components/svg/textDecorations';
import { restaurantTypes } from '../enums/restaurantTypes';
import { getTranslatedData } from '../localization';
import PaymentRating from '../features/payments/paymentRating';
import { useTableSseConnection } from '../hooks/useTableSseConnection';
import { apiActionChooser } from '../utils/apiActionChooser';

const HomePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  useTableSseConnection();

  const { state: restaurantState } = useContext(RestaurantContext);
  const { rateRestaurant } = useContext(CheckoutContext);
  const { state: authState, registerGuest } = useContext(AuthContext);
  const { alertController } = useContext(AlertContext);

  const reviewRef = useRef(null);

  const renderTitle = () => {
    if (!restaurantState.restaurant) {
      return undefined;
    }

    const customTitle = getTranslatedData(restaurantState.restaurant.customHomeTitle, {
      dontFillWithOtherLanguage: true,
    });
    if (customTitle) {
      return customTitle;
    }

    if (
      !restaurantState.restaurant.type ||
      restaurantState.restaurant.type === restaurantTypes.PayOnly ||
      restaurantState.restaurant.type === restaurantTypes.OrderAndPay
    ) {
      return (
        <Trans i18nKey="home.title">
          line1 part1
          <span className="relative">
            underlined word
            <PriceUnderline />
          </span>
          line1 part2 <br /> line2
        </Trans>
      );
    }

    if (restaurantState.restaurant.type === restaurantTypes.TipsOnly) {
      return (
        <Trans i18nKey="home.titleForTipsApp">
          line1 part1
          <span className="relative">
            underlined word
            <PriceUnderline />
          </span>
          line1 part2 <br /> line2
        </Trans>
      );
    }

    return undefined;
  };

  const openReviewForm = () => {
    reviewRef.current.openReviewForm();
  };

  const updateRating = async (rating, comment) => {
    let userToUse = authState.user;
    let tokenToUse = authState.token;

    if (!authState.user || !authState.token) {
      await apiActionChooser(
        global.localStorage.getItem('v2-company-id'),
        () => {
          // v1 no changes
        },
        async () => {
          const { token, user } = await registerGuest();
          tokenToUse = token;
          userToUse = user;
        },
      );
    }

    rateRestaurant({
      token: tokenToUse,
      userId: userToUse.id,
      restaurantId: restaurantState.restaurant.id,
      rating,
      comment,
      alertController,
    });
  };

  const renderButtons = () => {
    if (!restaurantState.restaurant) {
      return undefined;
    }

    if (
      !restaurantState.restaurant.type ||
      restaurantState.restaurant.type === restaurantTypes.PayOnly
    ) {
      return (
        <>
          <MainButton text={t('buttons.viewMenu')} outline onClick={() => navigate('/menu')} />
          <div className="mt-3" />
          <MainButton text={t('buttons.payTheBill')} onClick={() => navigate('/bill')} />
          {restaurantState.restaurant.allowTipsNoOrder && (
            <>
              <div className="mt-3" />
              <MainButton
                text={t('buttons.leaveTips')}
                onClick={() => navigate('/checkout-tips')}
              />
            </>
          )}
          {restaurantState.restaurant.allowReviewsNoOrder && (
            <>
              <div className="mt-3" />
              <MainButton text={t('buttons.leaveReview')} onClick={openReviewForm} />
            </>
          )}
        </>
      );
    }

    if (restaurantState.restaurant.type === restaurantTypes.TipsOnly) {
      return (
        <>
          <MainButton text={t('buttons.viewMenu')} outline onClick={() => navigate('/menu')} />
          {!restaurantState.restaurant.forceDisableTips && (
            <>
              <div className="mt-3" />
              <MainButton
                text={t('buttons.leaveTips')}
                onClick={() => navigate('/checkout-tips')}
              />
            </>
          )}
          {restaurantState.restaurant.allowReviewsNoOrder && (
            <>
              <div className="mt-3" />
              <MainButton text={t('buttons.leaveReview')} onClick={openReviewForm} />
            </>
          )}
        </>
      );
    }

    if (restaurantState.restaurant.type === restaurantTypes.OrderAndPay) {
      return (
        <>
          <MainButton text={t('buttons.viewMenu')} outline onClick={() => navigate('/menu')} />
          <div className="mt-3" />
          <MainButton text={t('buttons.recentOrders')} onClick={() => navigate('/orders')} />
          {restaurantState.restaurant.allowTipsNoOrder && (
            <>
              <div className="mt-3" />
              <MainButton
                text={t('buttons.leaveTips')}
                onClick={() => navigate('/checkout-tips')}
              />
            </>
          )}
          {restaurantState.restaurant.allowReviewsNoOrder && (
            <>
              <div className="mt-3" />
              <MainButton text={t('buttons.leaveReview')} onClick={openReviewForm} />
            </>
          )}
        </>
      );
    }

    return undefined;
  };

  // No table found in DB
  if (restaurantState.tableGot && !restaurantState.table) {
    return (
      <MainLayout>
        <div className="mx-auto mt-20">
          <h1 className="font-primaryMedium text-2xl text-primary text-center">
            {t('home.noTableScanned')}
          </h1>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout showPayments>
      <RestaurantHeader restaurant={restaurantState.restaurant} table={restaurantState.table} />
      <div className="mx-auto mt-12">
        <h1 className="font-primaryMedium text-2xl text-primary text-center">{renderTitle()}</h1>
      </div>

      <div className="w-full mt-8 max-w-xs mx-auto">{renderButtons()}</div>

      {restaurantState.restaurant && restaurantState.restaurant.allowReviewsNoOrder && (
        <PaymentRating
          ref={reviewRef}
          restaurantName={(restaurantState.restaurant || {}).name || ''}
          onUpdateRating={updateRating}
          reviewLink={restaurantState.restaurant.reviewLink}
          fromHome
        />
      )}
    </MainLayout>
  );
};

export default HomePage;
